import { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import * as Popover from '@radix-ui/react-popover';
import { format, isBefore, parse } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/cn';
import { getDateLocale } from '@/utils/dateFormat';
import { Icon } from '@/components/ui/Icon';
import { TimePicker } from '@/components/ui/TimePicker';
import { ICONS } from '@/constants/icons';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { MOBILE_MODAL_POPOVER_COLLISION_PADDING } from '@/components/ui/popoverPlacement';
import 'react-day-picker/style.css';

/**
 * SchedulePicker Component
 *
 * Select scheduling options for posts (Publish Now or Schedule)
 *
 * Uses timezone cascade from TimezoneContext:
 * 1. User timezone (highest priority)
 * 2. Team timezone
 * 3. Default (Europe/Berlin)
 */

interface SchedulePickerProps {
  scheduledTime: Date | null;
  onScheduleChange: (time: Date | null) => void;
  disabled?: boolean;
  className?: string;
  /** When true, show datetime picker directly without mode selection */
  directMode?: boolean;
  /** When true, stack date and time vertically (for narrow containers) */
  compact?: boolean;
}

export function SchedulePicker({
  scheduledTime,
  onScheduleChange,
  disabled = false,
  className = '',
  directMode = false,
  compact = false,
}: SchedulePickerProps) {
  const { t } = useTranslation('common');
  const isNarrow = useMediaQuery('(max-width: 639px), (max-height: 500px)');

  // Get timezone from cascade (user → team → default)
  const { timezone, timezoneAbbreviation } = useTimezone();

  // Derive a team-tz date string (yyyy-MM-dd) from a UTC Date.
  const toDateStr = (date: Date): string =>
    formatInTimeZone(date, timezone, 'yyyy-MM-dd');

  // Derive a team-tz time string (HH:mm) from a UTC Date.
  const toTimeStr = (date: Date): string =>
    formatInTimeZone(date, timezone, 'HH:mm');

  // Produce a UTC Date by treating dateStr+time as a wall-clock instant in team-tz.
  const buildUTC = (dateStr: string, time: string): Date =>
    fromZonedTime(`${dateStr}T${time}:00`, timezone);

  // Helper: compute the default future time (next whole hour) in team-tz.
  const getDefaultFuture = (): { dateStr: string; time: string } => {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setMinutes(0, 0, 0);
    nextHour.setHours(nextHour.getHours() + 1);
    return { dateStr: toDateStr(nextHour), time: toTimeStr(nextHour) };
  };

  const defaultFuture = !scheduledTime ? getDefaultFuture() : null;

  // If directMode, always treat as 'schedule' mode.
  const [mode, setMode] = useState<'now' | 'schedule'>(
    directMode ? 'schedule' : (scheduledTime ? 'schedule' : 'now')
  );
  // Team-tz calendar date kept as "yyyy-MM-dd" — immune to browser-tz interpretation.
  const [selectedDateStr, setSelectedDateStr] = useState<string>(
    scheduledTime ? toDateStr(scheduledTime) : (defaultFuture?.dateStr ?? toDateStr(new Date()))
  );
  const [selectedTime, setSelectedTime] = useState<string>(
    scheduledTime ? toTimeStr(scheduledTime) : (defaultFuture?.time ?? '14:00')
  );
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState<Date>(() =>
    parse(selectedDateStr, 'yyyy-MM-dd', new Date())
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  // selectedDate as a browser-local Date for DayPicker integration only.
  // We parse the yyyy-MM-dd string as a local date so DayPicker highlights
  // the correct calendar cell regardless of browser timezone. The actual UTC
  // instant used for storage always comes from buildUTC(selectedDateStr, ...).
  const selectedDateForPicker = (): Date =>
    parse(selectedDateStr, 'yyyy-MM-dd', new Date());
  const todayForPicker = parse(toDateStr(new Date()), 'yyyy-MM-dd', new Date());

  const handleCalendarOpenChange = (open: boolean) => {
    // Start each visit at the selected date, while letting people browse other
    // months without snapping back during the same visit.
    if (open) setCalendarMonth(selectedDateForPicker());
    setShowCalendar(open);
  };

  // Sync with external scheduledTime changes (prop-driven updates from parent).
  // Re-derive both date and time purely via team-tz formatters — no browser-tz
  // involvement — so round-tripping through the parent never drifts the date.
  useEffect(() => {
    if (scheduledTime) {
      setMode('schedule');
      const newDateStr = toDateStr(scheduledTime);
      const newTime = toTimeStr(scheduledTime);
      setSelectedDateStr(newDateStr);
      setSelectedTime(newTime);
    } else if (!directMode) {
      setMode('now');
    }
    // toDateStr/toTimeStr are stable closures over `timezone`; no need to list them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduledTime, directMode, timezone]);

  const handleModeChange = (newMode: 'now' | 'schedule') => {
    setMode(newMode);
    setValidationError(null);

    if (newMode === 'now') {
      onScheduleChange(null);
    } else {
      const utc = buildUTC(selectedDateStr, selectedTime);
      if (validateDateTime(utc)) {
        onScheduleChange(utc);
      }
    }
  };

  const handleDateSelect = (date: Date | undefined) => {
    // Single mode can yield undefined if the selected day is chosen again.
    // Retain the date but dismiss the calendar.
    setShowCalendar(false);
    if (!date) return;

    // DayPicker's Date represents a calendar cell in the browser's timezone. Read
    // its calendar fields before converting the chosen wall-clock time to UTC;
    // formatting this Date in the account timezone can move it to yesterday.
    const newDateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    setSelectedDateStr(newDateStr);

    const utc = buildUTC(newDateStr, selectedTime);
    if (validateDateTime(utc)) {
      onScheduleChange(utc);
    } else {
      onScheduleChange(null);
    }
  };

  const handleTimeChange = (time: string) => {
    setSelectedTime(time);

    const utc = buildUTC(selectedDateStr, time);
    if (validateDateTime(utc)) {
      onScheduleChange(utc);
    } else {
      onScheduleChange(null);
    }
  };

  const validateDateTime = (utc: Date): boolean => {
    const now = new Date();

    if (isBefore(utc, now)) {
      setValidationError(t('schedule_error_past'));
      return false;
    }

    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
    if (utc > oneYearFromNow) {
      setValidationError(t('schedule_error_future_limit'));
      return false;
    }

    setValidationError(null);
    return true;
  };

  return (
    <div className={className}>
      {/* Only show header and mode selection if NOT in directMode */}
      {!directMode && (
        <>
          {/* Header */}
          <h3 className="text-sm font-semibold text-gray-800 mb-4">
            {t('schedule_when_to_publish')}
          </h3>

          {/* Mode Selection */}
          <div className="space-y-3 mb-4">
            <label
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                mode === 'now'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name="schedule-mode"
                checked={mode === 'now'}
                onChange={() => handleModeChange('now')}
                disabled={disabled}
                className="w-4 h-4 text-primary-500 focus:ring-primary-500"
              />
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">{t('schedule_publish_now')}</div>
                <div className="text-xs text-gray-500">
                  {t('schedule_publish_now_desc')}
                </div>
              </div>
            </label>

            <label
              className={cn(
                'flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all',
                mode === 'schedule'
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200 bg-white hover:border-gray-300',
                disabled && 'opacity-50 cursor-not-allowed'
              )}
            >
              <input
                type="radio"
                name="schedule-mode"
                checked={mode === 'schedule'}
                onChange={() => handleModeChange('schedule')}
                disabled={disabled}
                className="w-4 h-4 text-primary-500 focus:ring-primary-500"
              />
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">
                  {t('schedule_for_later')}
                </div>
                <div className="text-xs text-gray-500">
                  {t('schedule_for_later_desc')}
                </div>
              </div>
            </label>
          </div>
        </>
      )}

      {/* Schedule Options - Always show in directMode, otherwise only when mode === 'schedule' */}
      {(directMode || mode === 'schedule') && (
        <div className={cn(
          'space-y-4',
          !directMode && 'mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200'
        )}>
          {/* Date and Time Pickers */}
          <div className={cn('grid', compact ? 'grid-cols-2 gap-3' : 'grid-cols-1 sm:grid-cols-2 gap-4')}>
            {/* Date Picker */}
            <div>
              {!compact && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Icon name={ICONS.CALENDAR} size="sm" className="inline mr-1" />
                  {t('schedule_date')}
                </label>
              )}
              <Popover.Root open={showCalendar} onOpenChange={handleCalendarOpenChange}>
                <Popover.Trigger asChild>
                  <button
                    type="button"
                    disabled={disabled}
                    className={cn(
                      'w-full h-[42px] px-4 text-sm text-left border-2 rounded-lg transition-all duration-200',
                      'focus:outline-none focus:border-primary-500',
                      'hover:border-gray-400',
                      'disabled:opacity-50 disabled:cursor-not-allowed',
                      'whitespace-nowrap truncate bg-white',
                      showCalendar ? 'border-primary-500' : 'border-gray-300'
                    )}
                  >
                    {format(selectedDateForPicker(), 'EEE, MMM d, yyyy', { locale: getDateLocale() })}
                  </button>
                </Popover.Trigger>
                <Popover.Portal>
                  <Popover.Content
                    className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 z-popover-top date-picker-compact data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
                    side={directMode || isNarrow ? 'top' : 'bottom'}
                    sideOffset={8}
                    align="start"
                    collisionPadding={directMode || isNarrow
                      ? MOBILE_MODAL_POPOVER_COLLISION_PADDING
                      : 16}
                  >
                    <div
                      className="overflow-y-auto overscroll-contain"
                      style={{ maxHeight: 'calc(var(--radix-popover-content-available-height) - 24px)' }}
                    >
                      <DayPicker
                        mode="single"
                        required
                        month={calendarMonth}
                        onMonthChange={setCalendarMonth}
                        selected={selectedDateForPicker()}
                        onSelect={handleDateSelect}
                        disabled={{ before: todayForPicker }}
                        captionLayout="label"
                        weekStartsOn={1}
                        showWeekNumber
                        locale={getDateLocale()}
                      />
                    </div>
                    <Popover.Arrow className="fill-white" />
                  </Popover.Content>
                </Popover.Portal>
              </Popover.Root>
            </div>

            {/* Time Picker */}
            <div>
              {!compact && (
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Icon name={ICONS.CLOCK} size="sm" className="inline mr-1" />
                  {t('schedule_time')}
                </label>
              )}
              <TimePicker
                value={selectedTime}
                onChange={handleTimeChange}
                disabled={disabled}
                timezone={timezone}
                timezoneAbbreviation={timezoneAbbreviation}
                selectedDate={selectedDateForPicker()}
                allowPast={false}
              />
            </div>
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="text-xs text-danger bg-red-50 px-3 py-2 rounded border border-red-200">
              {validationError}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
